(ns manage.ad-variant-preview.widgets.mentions
  (:require ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/MenuList$default" :as MenuList]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Popper$default" :as Popper]
            ["slate" :as slate]
            ["slate-react" :as slate-react]
            [cljs-bean.core :refer [->clj ->js]]
            [common.queries :refer [use-query]]
            [common.resource.query.user :as user-queries]
            [common.util :refer [spread-obj]]
            [manage.slate-editor.codec :as codec]
            [manage.slate-editor.core :as slate-editor.core]
            [manage.slate-editor.mentions :as mentions]
            [manage.slate-editor.mentions-node :as mentions-node]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

;; This is based on: https://www.slatejs.org/examples/mentions

(defn- user-query-select [^js data]
  (mapv #(select-keys % [:id :name :email])
        (->clj data)))

(defn- user-query-allowed-ids-select [^js data]
  (into #{}
        (map :id)
        (->clj data)))

(defui message-preview [{:keys [value]}]
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        [^js editor _] (uix/use-state (fn []
                                        (-> (slate/createEditor)
                                            slate-react/withReact
                                            mentions/withMentions)))
        {allowed-ids :data} (use-query (spread-obj (user-queries/users-query {:agency-id agency-id})
                                                   #js {:select user-query-allowed-ids-select}))
        render-element (uix/use-callback (fn [props]
                                           (let [attributes (.-attributes props)
                                                 element (.-element props)
                                                 children (.-children props)]
                                             (case (.-type element)
                                               "mention" ($ mentions-node/mention {:user-id (.-id element)})
                                               ($ :p attributes
                                                  children))))
                                         [])

        initial-value (uix/use-memo (fn []
                                      (->js (codec/text->slate-value value {:allow-mentions true
                                                                            :allowed-mentions-ids allowed-ids})))
                                    [value allowed-ids])]

    ($ slate-react/Slate {:editor editor
                          :initialValue initial-value}
       ($ slate-react/Editable {:readOnly true
                                :renderElement render-element}))))

(defui mentions [{:keys [ref on-change placeholder initial-value allowed-ids sx]}]
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        input-ref (uix/use-ref)
        editor-ref (uix/use-ref)
        [target set-target] (uix/use-state nil)
        [search set-search] (uix/use-state "")
        {users :data} (use-query (spread-obj (user-queries/users-query {:agency-id agency-id})
                                             #js {:select user-query-select}))
        [active-idx set-active-idx] (uix/use-state 0)
        filtered-users (uix/use-memo (fn []
                                       (->> users
                                            (filter (comp allowed-ids :id))
                                            (mentions/search-users search)))
                                     [search users allowed-ids])

        on-select-user (uix/use-callback (fn [editor idx]
                                           (slate/Transforms.select editor target)
                                           (mentions/insert-mention editor (nth filtered-users idx))
                                           (set-active-idx 0)
                                           (set-target nil))
                                         [target filtered-users])

        on-key-down (uix/use-callback (fn [^js editor ^js event]
                                        ;; This is a hack. I am not sure about how to solve it in any other way?
                                        (when (= (.-key event) " ")
                                          (.insertText editor " "))

                                        (when (and target (seq filtered-users))
                                          (case (.-key event)
                                            "ArrowDown"
                                            (do
                                              (.preventDefault event)
                                              (set-active-idx #(mod (inc %) (count filtered-users))))

                                            "ArrowUp"
                                            (do
                                              (.preventDefault event)
                                              (set-active-idx #(mod (dec %) (count filtered-users))))

                                            ("Tab" "Enter")
                                            (do
                                              (.preventDefault event)
                                              (on-select-user editor active-idx))

                                            "Escape"
                                            (do
                                              (.preventDefault event)
                                              (.stopPropagation event)
                                              (set-target nil))

                                            nil)))
                                      [filtered-users target on-select-user active-idx])

        search-for-mentions (uix/use-callback (fn [^js editor]
                                                (let [selection (.-selection editor)]
                                                  (when (and selection (slate/Range.isCollapsed selection))
                                                    (let [[start] (slate/Range.edges selection)
                                                          before-point (slate/Editor.before editor start #js {:unit "character"})
                                                          before-range (when before-point
                                                                         (slate/Editor.range editor before-point start))
                                                          before-char (when before-range
                                                                        (slate/Editor.string editor before-range))

                                                          word-before (slate/Editor.before editor start #js {:unit "word"})
                                                          before (when word-before
                                                                   (slate/Editor.before editor word-before))
                                                          word-before-range (when before
                                                                              (slate/Editor.range editor before start))
                                                          word-before-text (when word-before-range
                                                                             (slate/Editor.string editor word-before-range))
                                                          word-before-match (when word-before-text
                                                                              (re-find #"^@(\w+)?$" word-before-text))

                                                          after (slate/Editor.after editor start)
                                                          after-range (slate/Editor.range editor start after)
                                                          after-text (slate/Editor.string editor after-range)

                                                          after-match (re-find #"^(\s|$)" after-text)]

                                                      (cond
                                                        (and (= before-char "@") after-match)
                                                        (do
                                                          (set-target before-range)
                                                          (set-search ""))

                                                        (and word-before-match after-match)
                                                        (do
                                                          (set-target word-before-range)
                                                          (set-search (second word-before-match)))

                                                        :else
                                                        (set-target nil))))))
                                              [])

        on-change-handler (uix/use-callback (fn [^js editor]
                                              (search-for-mentions editor)
                                              (-> editor .-children codec/slate-value->text on-change))
                                            [search-for-mentions on-change])]

    (uix/use-effect (fn []
                      (let [editor @editor-ref
                            dom-node (.toDOMNode slate-react/ReactEditor editor editor)]
                        (reset! input-ref dom-node)
                        (cond
                          (fn? ref) (ref dom-node)
                          (satisfies? IDeref ref) (reset! ref dom-node))))
                    [ref])

    ($ :<>
       ($ slate-editor.core/slate-editor {:editor-ref editor-ref
                                          :value initial-value
                                          :multiline true
                                          :auto-focus? true
                                          :allow-query-params false
                                          :allow-macros false
                                          :allow-mentions true
                                          :allowed-mentions-ids allowed-ids
                                          :placeholder placeholder
                                          :on-change on-change-handler
                                          :on-key-down on-key-down
                                          :sx sx})
       ($ Popper {:open (boolean (and (some? @input-ref)
                                      (some? target)
                                      (seq filtered-users)))
                  :placement "top"
                  :anchorEl @input-ref
                  :sx #js {:zIndex 2000 ; Seems like the drawer has the higher z-index
                           :width (some-> @input-ref .getBoundingClientRect .-width)}}
          ($ Paper {:elevation 2}
             ($ MenuList
                (map-indexed (fn [idx {:keys [name id]}]
                               ($ MenuItem {:key id
                                            :onMouseDown (fn [^js e]
                                                           (.preventDefault e) ; avoids blurring the Slate component
                                                           (on-select-user @editor-ref idx))
                                            :selected (= idx active-idx)}
                                  name))
                             filtered-users)))))))

